import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, ArrowLeft, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import LanguageSwitcher from '../components/LanguageSwitcher';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleaned = phone.replace(/\D/g, '').replace(/^91/, '');
    if (!cleaned || cleaned.length !== 10) {
      setError(t('enterValidPhone'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API_URL}/api/email/forgot-password`, { phone: cleaned });
      setSent(true);
      if (res.data.email) {
        setMaskedEmail(res.data.email);
      }
    } catch (err) {
      setError(err.response?.data?.detail || t('somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="absolute top-4 right-4"><LanguageSwitcher /></div>
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full" data-testid="forgot-password-page">
        <button onClick={() => navigate('/login')} className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-6">
          <ArrowLeft className="w-4 h-4 mr-1" /> {t('backToLogin')}
        </button>

        <h2 className="text-2xl font-bold text-gray-900 mb-2">{t('resetPassword')}</h2>

        {!sent ? (
          <>
            <p className="text-sm text-gray-500 mb-6">{t('forgotPasswordDesc')}</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm" data-testid="forgot-error">{error}</div>}
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={t('phoneNumber')}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  data-testid="forgot-phone-input"
                />
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-green-600 hover:bg-green-700 text-white py-3" data-testid="forgot-submit-btn">
                {loading ? t('sending') : t('sendResetLink')}
              </Button>
            </form>
          </>
        ) : (
          <div className="text-center py-4" data-testid="forgot-success">
            <Mail className="w-12 h-12 text-green-600 mx-auto mb-3" />
            {maskedEmail ? (
              <>
                <p className="text-gray-700 font-medium mb-2">{t('resetLinkSent')}</p>
                <p className="text-sm text-gray-500 mb-1">{t('resetLinkSentDesc')}</p>
                <p className="text-base font-semibold text-gray-900 mb-3" data-testid="forgot-masked-email">{maskedEmail}</p>
                <p className="text-xs text-gray-400">{t('linkExpiresIn1Hour')}</p>
              </>
            ) : (
              <>
                <p className="text-gray-700 font-medium mb-2">{t('requestReceived')}</p>
                <p className="text-sm text-gray-500">{t('resetLinkGenericMsg')}</p>
              </>
            )}
            <button onClick={() => navigate('/login')} className="mt-4 text-green-600 underline text-sm">{t('backToLogin')}</button>
          </div>
        )}
      </div>
    </div>
  );
}
